import { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Edit2, MessageSquare, Upload, X, Image as ImageIcon } from 'lucide-react';

const MESSAGE_LABELS = {
  'WelcomeMessage': 'Karşılama Mesajı',
  'BrandsMessage': 'Marka Mesajı',
  'CategoriesMessage': 'Kategori Mesajı',
  'NewProductsAnnouncement': 'Yeni Ürünler Duyurusu',
  'SaleProductsAnnouncement': 'İndirimli Ürünler Duyurusu'
};

// Messages that support image attachment (menu sections)
const IMAGE_SUPPORTED_KEYS = ['WelcomeMessage', 'BrandsMessage', 'CategoriesMessage'];

export default function Messages({ selectedTeam }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [formData, setFormData] = useState({ value: '', image_url: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (selectedTeam) {
      loadMessages();
    }
  }, [selectedTeam]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/teams/${selectedTeam}/messages`);
      const predefinedKeys = Object.keys(MESSAGE_LABELS);
      const filteredMessages = res.data.filter(msg => predefinedKeys.includes(msg.key));
      setMessages(filteredMessages);
    } catch (err) {
      toast.error('Mesajlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const supportsImage = editingMessage && IMAGE_SUPPORTED_KEYS.includes(editingMessage.key);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // When image is supported and provided, text can be empty.
    // When image is not supported OR no image, text required for the message to be visible.
    const payload = { value: formData.value || '' };
    if (supportsImage) {
      payload.image_url = formData.image_url || '';
    }
    try {
      await axiosInstance.put(`/teams/${selectedTeam}/messages/${editingMessage.id}`, payload);
      toast.success('Mesaj güncellendi');
      setDialogOpen(false);
      setFormData({ value: '', image_url: '' });
      setEditingMessage(null);
      loadMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleEdit = (message) => {
    setEditingMessage(message);
    setFormData({
      value: message.value || '',
      image_url: message.image_url || ''
    });
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Sadece resim yükleyebilirsiniz');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axiosInstance.post('/upload-media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, image_url: res.data.url }));
      toast.success('Resim yüklendi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, image_url: '' }));
  };

  if (!selectedTeam) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="w-96 text-center overflow-hidden">
          <CardContent className="pt-12 pb-12">
            <MessageSquare className="w-16 h-16 mx-auto mb-4 text-purple-500" />
            <h2 className="text-2xl font-bold mb-2">Mağaza Seçin</h2>
            <p className="text-gray-600">Mesajları yönetmek için bir mağaza seçin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isAnnouncementTemplate = editingMessage && (
    editingMessage.key === 'NewProductsAnnouncement' ||
    editingMessage.key === 'SaleProductsAnnouncement'
  );

  return (
    <div className="p-8" data-testid="messages-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Mesaj Şablonları</h1>
        <p className="text-gray-600">Bot mesaj şablonlarınızı düzenleyin</p>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="message-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mesajı Düzenle</DialogTitle>
            <DialogDescription>
              {editingMessage && MESSAGE_LABELS[editingMessage.key]} içeriğini düzenleyin
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {supportsImage && (
                <div className="space-y-2">
                  <Label>Resim (Opsiyonel)</Label>
                  <p className="text-xs text-gray-500">
                    Resim eklerseniz mesaj metni altyazı (caption) olarak görünür. Mesaj metnini boş bırakırsanız sadece resim ve butonlar gönderilir.
                  </p>
                  {formData.image_url ? (
                    <div className="relative inline-block">
                      <img
                        src={formData.image_url}
                        alt="Önizleme"
                        className="max-h-48 rounded-2xl border border-gray-200"
                      />
                      <Button
                        type="button"
                        size="icon"
                        variant="destructive"
                        className="absolute top-2 right-2 h-8 w-8 rounded-full"
                        onClick={handleRemoveImage}
                        data-testid="remove-message-image-button"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2">
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploading}
                          className="hidden"
                          data-testid="message-image-upload-input"
                        />
                        <div className={`flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-2xl cursor-pointer transition-colors ${uploading ? 'bg-gray-100 border-gray-300' : 'border-purple-300 hover:border-purple-500 hover:bg-purple-50'}`}>
                          <Upload className="h-4 w-4 text-purple-500" />
                          <span className="text-sm font-medium text-purple-600">
                            {uploading ? 'Yükleniyor...' : 'Resim Yükle'}
                          </span>
                        </div>
                      </label>
                      <div className="flex items-center gap-2 text-gray-400 text-xs">
                        veya URL gir
                      </div>
                    </div>
                  )}
                  <Input
                    placeholder="https://..."
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    data-testid="message-image-url-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="value">
                  Mesaj İçeriği {supportsImage ? '(Opsiyonel - boş bırakılabilir)' : '*'}
                </Label>
                {isAnnouncementTemplate && (
                  <p className="text-xs text-gray-500">
                    Değişkenler: <code className="bg-gray-100 px-1 rounded">{'{brand}'}</code> = Marka adı
                    {editingMessage.key === 'NewProductsAnnouncement' && <>, <code className="bg-gray-100 px-1 rounded">{'{count}'}</code> = Ürün sayısı</>}
                  </p>
                )}
                <Textarea
                  id="value"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder={supportsImage ? 'İsteğe bağlı... (boş bırakılabilir)' : 'Mesaj içeriğini girin...'}
                  rows={isAnnouncementTemplate ? 12 : 6}
                  required={!supportsImage}
                  data-testid="message-value-input"
                  className="font-mono text-sm"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-gradient-to-r from-purple-500 to-pink-500" data-testid="save-message-button">
                Güncelle
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : messages.length === 0 ? (
        <Card className="text-center py-12 overflow-hidden">
          <CardContent>
            <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2">Hiç mesaj yok</h3>
            <p className="text-gray-600 mb-4">Yeni bir mesaj şablonu ekleyerek başlayın</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {messages.map(message => (
            <Card key={message.id} className="shadow-lg hover:shadow-xl transition-all border-0 overflow-hidden" data-testid={`message-card-${message.key}`}>
              <CardHeader className="bg-gradient-to-br from-purple-500 to-pink-500 text-white">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center">
                    <MessageSquare className="w-5 h-5 mr-2" />
                    {MESSAGE_LABELS[message.key] || message.key}
                  </CardTitle>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="hover:bg-white/20"
                    onClick={() => handleEdit(message)}
                    data-testid={`edit-message-${message.key}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                {message.image_url && (
                  <div className="flex items-start gap-2 p-2 bg-purple-50 rounded-xl">
                    <ImageIcon className="w-4 h-4 mt-0.5 text-purple-500 shrink-0" />
                    <img
                      src={message.image_url}
                      alt="Ekli resim"
                      className="max-h-32 rounded-lg"
                    />
                  </div>
                )}
                {message.value ? (
                  <p className="text-gray-700 whitespace-pre-wrap">{message.value}</p>
                ) : (
                  <p className="text-gray-400 italic text-sm">Mesaj metni boş — sadece resim gönderilecek.</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
